import * as React from 'react'
import type { LinkProps as ReactLinkProps } from 'react-router-dom'
import { Link as ReactLink } from 'react-router-dom'
import type { LocationDescriptor } from 'history'
import { omit, pick } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import { x } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'

export interface LinkProps
  extends HTMLXStyledProps<'a'>,
    Omit<ReactLinkProps, 'color'> {
  disabled?: boolean
  isExternal?: boolean
  underline?: 'none' | 'hover' | 'always'
}

const isExternalLink = (to: LocationDescriptor): boolean => {
  const path = typeof to === 'string' ? to : to.pathname

  if (!path) return false

  return (
    path.startsWith(`http://`) ||
    path.startsWith(`https://`) ||
    path.startsWith(`//`)
  )
}

type ReactLinkPropKeys = Array<
  keyof Pick<LinkProps, 'component' | 'replace' | 'innerRef'>
>
const REACTLINK_PROPS: ReactLinkPropKeys = ['component', 'replace', 'innerRef']

export const Link = forwardRef<LinkProps, 'a'>(
  (
    {
      isExternal = false,
      fontSize = 'md',
      lineHeight = 'md',
      color = 'black',
      fontWeight = 'normal',
      underline = 'none',
      disabled = false,
      to,
      ...props
    },
    ref
  ): JSX.Element => {
    const reactLinkProps = pick(props, REACTLINK_PROPS)
    const shared: any = {
      disabled,
      fontSize,
      ref,
      fontWeight,
      lineHeight,
      color,
      transition: true,
      cursor: 'pointer',
      outline: 'transparent solid 2px',
      outlineOffset: '2px',
      boxShadow: {
        _: 'none',
        focus: 'outline-primary'
      },
      ...omit(props, REACTLINK_PROPS),
      textDecoration: {
        _: underline === 'always' ? 'underline' : 'none',
        hover: ['hover', 'always'].includes(underline) ? 'underline' : 'none'
      }
    }

    if (disabled) {
      shared.href = '#!'
    }

    if (props.target === '_blank') {
      return (
        <x.a
          href={to as string}
          {...shared}
          rel='noopener noreferrer'
          target='_blank'
        />
      )
    }

    if (disabled || isExternal || isExternalLink(to as LocationDescriptor)) {
      return <x.a href={to as string} {...shared} />
    }

    return <x.a as={ReactLink} to={to} {...shared} {...reactLinkProps} />
  }
)

if (__DEV__) {
  Link.displayName = 'Link'
}
